'use client';

import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Image from 'next/image';
import type { CategoryItem } from '@/lib/content-schema';

interface Props {
  categories: CategoryItem[];
}

export function CategoryCarousel({ categories }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  const x = useTransform(scrollYProgress, [0, 1], ['0%', '-75%']);

  return (
    <section ref={containerRef} className="relative h-[300vh] bg-zibarit-verde-oscuro">
      <div className="sticky top-0 h-screen overflow-hidden flex items-center">
        <motion.div style={{ x }} className="flex gap-6 pl-8 md:pl-16">
          {categories.map((category, index) => (
            <div
              key={category.key}
              className="relative flex-shrink-0 w-[80vw] md:w-[60vw] lg:w-[45vw] h-[70vh] rounded-2xl overflow-hidden group"
            >
              {/* Background Image */}
              <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-105">
                <Image
                  src={category.image}
                  alt={category.label}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 80vw, (max-width: 1024px) 60vw, 45vw"
                  priority={index === 0}
                />
              </div>

              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-zibarit-negro/80 via-transparent to-transparent" />

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                <span
                  className="inline-block px-3 py-1 rounded-full text-xs font-lato font-bold uppercase tracking-wider text-white mb-4"
                  style={{ backgroundColor: category.color }}
                >
                  {category.label}
                </span>
                <h3 className="text-title text-3xl md:text-4xl lg:text-5xl text-zibarit-bone">
                  {category.title}
                </h3>
                <p className="text-body text-sm md:text-base text-zibarit-marron-secundario mt-3 max-w-md">
                  {category.description}
                </p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
